import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Search, UserPlus } from "lucide-react";
import contactservice from "../../store/services/contact.service";
import { generalActions } from "../../store/actions";
import CustomButton from "../controls/button/Button";
import CustomInput from "../controls/input/customInput/CustomInput";
import Modal from "../controls/modal/Modal";
import Spin from "../controls/spinner/Spin";
import ContactCardList from "./ContactCardList";
import ContactOrgChart from "./ContactOrgChart";
import ContactFormModal from "./ContactFormModal";
import styles from "./ContactsPanel.module.scss";

// parentType: "Clients" | "Portfolios" | "Programs" | "Projects"
// embedded: true when nested inside another card (e.g. the Details tab's left panel) -
// skips its own outer card chrome since the parent already provides it.
// view/onViewChange: pass these to let a parent render the Cards/Org Chart toggle itself
// (e.g. next to its own Details/Contact toggle) instead of ContactsPanel rendering one.
const ContactsPanel = ({ parentType, parentId, parentName, embedded = false, view: controlledView, onViewChange }) => {
  const dispatch = useDispatch();
  const [internalView, setInternalView] = useState("cards");
  const view = controlledView || internalView;
  const setView = onViewChange || setInternalView;
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactPendingDelete, setContactPendingDelete] = useState(null);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const fetchContacts = useCallback(
    (term) => {
      setIsLoading(true);
      contactservice
        .getViewContacts(parentType, parentId, term)
        .then((response) => setContacts(response || []))
        .catch(() =>
          dispatch(
            generalActions.showToast({
              title: "Couldn't load contacts. Please try again later.",
              type: "error",
            })
          )
        )
        .finally(() => setIsLoading(false));
    },
    [parentType, parentId, dispatch]
  );

  // Single effect for both the initial/entity-switch load and debounced search, so mounting
  // (or switching entities) only fires one request instead of two - an empty searchTerm skips
  // the debounce delay so the initial load still feels instant.
  useEffect(() => {
    if (!parentType || !parentId) return;
    const timeoutId = setTimeout(() => fetchContacts(searchTerm), searchTerm ? 400 : 0);
    return () => clearTimeout(timeoutId);

  }, [parentType, parentId, searchTerm]);

  const openCreate = () => {
    setEditingContact(null);
    setShowFormModal(true);
  };

  const openEdit = (contact) => {
    setEditingContact(contact);
    setShowFormModal(true);
  };

  const handleFormSaved = () => {
    setShowFormModal(false);
    setEditingContact(null);
    fetchContacts(searchTerm);
  };

  const confirmDelete = () => {
    if (!contactPendingDelete) return;
    setIsActionLoading(true);
    contactservice
      .deleteContact(parentType, parentId, contactPendingDelete.id)
      .then(() => {
        dispatch(generalActions.showToast({ title: "Contact deleted", type: "success" }));
        setContactPendingDelete(null);
        fetchContacts(searchTerm);
      })
      .catch(() =>
        dispatch(
          generalActions.showToast({ title: "Something went wrong. Please try again later.", type: "error" })
        )
      )
      .finally(() => setIsActionLoading(false));
  };

  return (
    <div className={`${embedded ? "" : "CustomCard p-4 mt-4"} ${styles.contactsPanel}`}>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-20">
        <div className="d-flex align-items-center gap-2">
          {!embedded && <h4 className="mb-0">Contacts</h4>}
          {!controlledView && (
            <div className="resourcesTabToggleContainer">
              <button
                className={`resourcesTabToggleButton ${view === "cards" ? "active" : ""}`}
                onClick={() => setView("cards")}
              >
                Cards
              </button>
              <button
                className={`resourcesTabToggleButton ${view === "orgChart" ? "active" : ""}`}
                onClick={() => setView("orgChart")}
              >
                Org Chart
              </button>
            </div>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <CustomButton
            btnClass="d_btn d_btn_primaryXsm"
            icon={<UserPlus size={14} />}
            label="Add Contact"
            clickHandler={openCreate}
          />
        </div>
      </div>

      <CustomInput
        inputwd="mb-3"
        placeholder="Search contacts"
        value={searchTerm}
        handleChange={setSearchTerm}
        icon={<Search size={16} />}
        dir="right"
      />

      {isLoading ? (
        <div className="d-flex justify-content-center py-4">
          <Spin />
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-4 text-gray-500">
          <p className="text-sm">No contacts yet</p>
        </div>
      ) : view === "cards" ? (
        <ContactCardList
          contacts={contacts}
          parentType={parentType}
          onEdit={openEdit}
          onDelete={setContactPendingDelete}
        />
      ) : (
        <ContactOrgChart
          contacts={contacts}
          parentType={parentType}
          parentId={parentId}
          parentName={parentName}
          onEdit={openEdit}
          onDelete={setContactPendingDelete}
        />
      )}

      {showFormModal && (
        <ContactFormModal
          parentType={parentType}
          parentId={parentId}
          contact={editingContact}
          handleCancel={() => setShowFormModal(false)}
          handleSaved={handleFormSaved}
        />
      )}

      {contactPendingDelete && (
        <Modal
          title="Delete contact?"
          handleCancel={() => setContactPendingDelete(null)}
          handleSave={confirmDelete}
          saveBtnLabel="Delete"
          cancelBtnLabel="Cancel"
          isLoading={isActionLoading}
          saveBtnClass="d_btn_dangerXsm"
          body={
            <p className="text-md-400 grey10Color">
              This permanently deletes <strong>{contactPendingDelete.name || contactPendingDelete.email}</strong>.
              If it was shared, it will no longer be visible anywhere in the hierarchy. This can't be undone.
            </p>
          }
        />
      )}
    </div>
  );
};

export default ContactsPanel;
